import React, { useEffect, useState } from "react";
import { useSearchParams, NavLink } from "react-router-dom";
import {
  downloadInvoiceBySession,
  downloadReceiptBySession,
  receiptFilename,
  getOrderBySession,
  Order,
  triggerBlobDownload,
} from "../../api/orders";
import {
  getRegistration,
  RegistrationRecord,
} from "../../api/registration";
import getSymbolFromCurrency from "currency-symbol-map";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadState, setDownloadState] = useState<
    "receipt" | "invoice" | null
  >(null);
  const [pollCount, setPollCount] = useState(0);
  const [registration, setRegistration] = useState<RegistrationRecord | null>(
    null,
  );
  const pendingRegistrationId = sessionStorage.getItem("pendingRegistrationId");

  useEffect(() => {
    if (!sessionId) {
      setError("Missing checkout session.");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadOrder = async () => {
      try {
        const result = await getOrderBySession(sessionId);
        if (cancelled) return;
        setOrder(result);
        setError(null);

        if (result.status !== "paid" && pollCount < 15) {
          setTimeout(() => setPollCount((c) => c + 1), 2000);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            getUserFacingErrorMessage(err, "Unable to load your order."),
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadOrder();

    return () => {
      cancelled = true;
    };
  }, [sessionId, pollCount]);

  useEffect(() => {
    if (!pendingRegistrationId) return;
    let cancelled = false;

    const loadRegistration = async () => {
      try {
        const result = await getRegistration(pendingRegistrationId);
        if (cancelled) return;
        setRegistration(result);
        if (result.status !== "confirmed" && pollCount < 15) {
          setTimeout(() => setPollCount((c) => c + 1), 2000);
        } else if (result.status === "confirmed") {
          sessionStorage.removeItem("pendingRegistrationId");
          sessionStorage.removeItem("pendingRegistrationEventSlug");
          sessionStorage.removeItem("pendingCheckoutType");
        }
      } catch {
        // Registration polling is best-effort on the success page
      }
    };

    loadRegistration();
    return () => {
      cancelled = true;
    };
  }, [pendingRegistrationId, pollCount]);

  const handleDownload = async (type: "receipt" | "invoice") => {
    if (!sessionId || !order) return;
    setDownloadState(type);
    try {
      let blob: Blob;
      let filename: string;
      if (type === "receipt") {
        blob = await downloadReceiptBySession(sessionId);
        filename = receiptFilename(order);
      } else {
        blob = await downloadInvoiceBySession(sessionId);
        filename = `${order.orderNumber ?? `order-${order.id}`}-invoice.pdf`;
      }
      triggerBlobDownload(blob, filename);
    } catch (err) {
      setError(
        getUserFacingErrorMessage(err, `Failed to download ${type}.`),
      );
    } finally {
      setDownloadState(null);
    }
  };

  if (isLoading && !order) {
    return (
      <div className="container mx-auto p-8 max-w-[800px]">
        <LoadingPanel
          title="Confirming your payment"
          subtitle="Waiting for Stripe to confirm your transaction. This usually takes a few seconds."
        />
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="container mx-auto p-8 max-w-[800px]">
        <h1 className="text-2xl font-bold mb-4">Payment Status</h1>
        <ErrorAlert>{error}</ErrorAlert>
        <NavLink to="/" className="underline mt-4 inline-block">
          Return home
        </NavLink>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const currency = order.currency.toUpperCase();
  const isPaid = order.status === "paid";

  return (
    <div className="container mx-auto p-8 max-w-[800px]">
      <h1 className="text-2xl font-bold mb-2">
        {isPaid ? "Payment Successful" : "Processing payment"}
      </h1>
      <p className="text-slate-600 mb-6">
        {isPaid ? (
          <>
            Thank you for your purchase. Your order{" "}
            <span className="font-semibold">
              {order.orderNumber ?? order.id}
            </span>{" "}
            has been confirmed.
          </>
        ) : (
          <>
            Your payment is being confirmed. This page will update automatically.
          </>
        )}
      </p>

      {registration && (
        <div className="border rounded-sm p-4 mb-6 bg-amber-50 border-amber-200">
          <p className="font-bold mb-2">
            {registration.status === "confirmed"
              ? "Registration confirmed"
              : "Registration pending confirmation"}
          </p>
          <p className="text-sm mb-1">
            Registration ID:{" "}
            <span className="font-semibold">{registration.registrationId}</span>
          </p>
          <p className="text-sm mb-1">
            {registration.firstName} {registration.lastName} —{" "}
            {registration.itemOrdered}
          </p>
          {registration.banquetQuantity > 0 && (
            <p className="text-sm text-slate-600">
              Includes {registration.banquetQuantity} banquet ticket
              {registration.banquetQuantity > 1 ? "s" : ""}
            </p>
          )}
          <p className="text-sm text-slate-600 mt-2">
            Event: {registration.event.title}
          </p>
        </div>
      )}

      <div className="border rounded-sm p-4 mb-6 bg-white">
        <p className="font-bold mb-3">Order summary</p>
        {order.orderItems.map((item) => (
          <div
            key={item.id}
            className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0"
          >
            <span>
              {item.event.title} — {item.ticketType.name} x{item.quantity}
            </span>
            <span>
              {getSymbolFromCurrency(currency)}
              {(item.subtotalPence / 100).toFixed(2)}
            </span>
          </div>
        ))}
        <div className="flex justify-between font-bold mt-4">
          <span>Total</span>
          <span>
            {getSymbolFromCurrency(currency)}
            {(order.totalAmountPence / 100).toFixed(2)}
          </span>
        </div>
      </div>

      {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        {isPaid && order.payment?.receiptPdfAvailable && (
          <button
            type="button"
            onClick={() => handleDownload("receipt")}
            disabled={downloadState !== null}
            className="bg-slate-800 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {downloadState === "receipt"
              ? "Downloading..."
              : "Download receipt (PDF)"}
          </button>
        )}
        {order.payment?.invoiceAvailable && (
          <button
            type="button"
            onClick={() => handleDownload("invoice")}
            disabled={downloadState !== null}
            className="border border-slate-300 px-4 py-2 rounded disabled:opacity-50"
          >
            {downloadState === "invoice"
              ? "Downloading..."
              : "Stripe invoice (PDF)"}
          </button>
        )}
        {order.payment?.receiptUrl && (
          <a
            href={order.payment.receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-slate-300 px-4 py-2 rounded text-center"
          >
            View Stripe receipt
          </a>
        )}
        <NavLink
          to="/user/orders"
          className="border border-slate-300 px-4 py-2 rounded text-center"
        >
          My orders &amp; registrations
        </NavLink>
      </div>

      {!isPaid && (
        <p className="text-sm text-slate-500 mt-4">
          Still processing… this page refreshes every few seconds.
        </p>
      )}
    </div>
  );
}
